import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Numbered Block wrapper — matches visual convention used in W3/W7/W14/W24/W31/W51/W59
function Block({ n, title, subtitle, children, testId }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={testId || `w28-block-${n}`}>
      <header className="bg-slate-800 text-white px-4 py-2.5">
        <h3 className="font-bold text-sm tracking-tight">
          <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {n}</span>
          {title}
        </h3>
        {subtitle && <p className="text-[11px] text-white/70 mt-0.5">{subtitle}</p>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function Window28CombinationValidator() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [w15Data, setW15Data] = useState(null);
  const [mainTab, setMainTab] = useState('analysis');

  useEffect(() => {
    (async () => {
      try {
        const [res, w15Res] = await Promise.all([
          axios.get(`${API}/analysis/combination-validator`),
          axios.get(`${API}/analysis/odd-even-high-low`),
        ]);
        setData(res.data);
        setW15Data(w15Res.data);
      } catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 28</h1><p className="text-xs text-gray-500">Combination Validator + O/E H/L Analysis</p></div>
            </div>
            <ExportButton data={data?.validated_combinations} filename="w28_validated_combos" />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W28" {...NARRATIVES.W28} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <div className="space-y-4">
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5" data-testid="w28-main-tabs">
              <button onClick={() => setMainTab('analysis')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'analysis' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-analysis">
                Validator & Gap Analysis
              </button>
              <button onClick={() => setMainTab('combos')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'combos' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-combos">
                Top Combos & Prediction
              </button>
              <button onClick={() => setMainTab('recent')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'recent' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-recent">
                Last 20 Draws
              </button>
            </div>

            {mainTab === 'analysis' && <ValidatorContent data={data} />}
            {mainTab === 'combos' && <TopCombosTab w15={w15Data} data={data} />}
            {mainTab === 'recent' && <RecentDrawsTab w15={w15Data} />}
          </div>
        ) : null}
      </main>
    </div>
  );
}

/* ========== Tab 1: Original W28 Validator ========== */
function ValidatorContent({ data }) {
  return (
    <div className="space-y-4">
      {/* Block 1 — Sum & Spread */}
      <Block n={1} title="Sum & Spread Filters" subtitle="Valid combinations must stay within ±1.5σ of historical averages">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-bold text-gray-900 mb-3 text-sm">Sum Range Filter</h4>
            <p className="text-xs text-gray-500 mb-2">Valid combinations must have sum within 1.5 standard deviations</p>
            <div className="grid grid-cols-3 gap-3" data-testid="sum-rules">
              <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-gray-500">Min</p><p className="font-mono font-bold text-lg">{data.rules.sum_range.min}</p></div>
              <div className="bg-blue-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-blue-600">Avg</p><p className="font-mono font-bold text-lg text-blue-700">{data.rules.sum_range.avg}</p></div>
              <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-gray-500">Max</p><p className="font-mono font-bold text-lg">{data.rules.sum_range.max}</p></div>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-3 text-sm">Spread Range Filter</h4>
            <p className="text-xs text-gray-500 mb-2">Distance between highest and lowest number</p>
            <div className="grid grid-cols-3 gap-3" data-testid="spread-rules">
              <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-gray-500">Min</p><p className="font-mono font-bold text-lg">{data.rules.spread_range.min}</p></div>
              <div className="bg-blue-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-blue-600">Avg</p><p className="font-mono font-bold text-lg text-blue-700">{data.rules.spread_range.avg}</p></div>
              <div className="bg-gray-50 rounded p-2 text-center"><p className="text-[10px] uppercase font-bold text-gray-500">Max</p><p className="font-mono font-bold text-lg">{data.rules.spread_range.max}</p></div>
            </div>
          </div>
        </div>
      </Block>

      {/* Block 2 — Odd/Even Balance */}
      <Block n={2} title="Odd/Even Balance — Gap Analysis" subtitle={`Till 2025 (${data.rules.draws_till_2025}) vs 2026 (${data.rules.draws_2026}). Sorted by highest %.`}>
        <div className="overflow-x-auto" data-testid="odd-even-rules">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-3">Pattern</th>
                  <th className="text-center py-2 px-2">Till 2025</th>
                  <th className="text-center py-2 px-2">2026</th>
                  <th className="text-center py-2 px-2">Diff</th>
                  <th className="text-center py-2 px-2">Last Seen</th>
                  <th className="text-center py-2 px-2">Gap</th>
                  <th className="text-center py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...(data.rules.oe_gap_analysis || [])].sort((a, b) => b.pct - a.pct).map(oe => {
                  const isOverdue = oe.overdue;
                  const yc = (data.rules.oe_year_comparison || []).find(y => y.pattern === oe.pattern);
                  const maxCount = Math.max(...(data.rules.odd_even || []).map(x => x.count));
                  return (
                    <tr key={oe.pattern} className={`border-b border-gray-100 ${isOverdue ? "bg-red-50" : ""}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold text-gray-800 w-14">{oe.pattern}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3 min-w-[40px]">
                            <div className={`h-3 rounded-full ${isOverdue ? "bg-red-500" : "bg-[#002FA7]"}`} style={{ width: `${(oe.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-2 text-center font-mono font-bold text-gray-600">{yc?.till_2025_pct ?? oe.pct}%</td>
                      <td className="py-2.5 px-2 text-center font-mono font-bold text-violet-700">{yc?.y2026_pct ?? 0}%</td>
                      <td className="py-2.5 px-2 text-center">
                        {yc && <span className={`font-mono font-bold text-xs px-2 py-0.5 rounded ${yc.diff > 0 ? "text-emerald-700 bg-emerald-50" : yc.diff < 0 ? "text-red-600 bg-red-50" : "text-gray-400"}`}>{yc.diff > 0 ? "+" : ""}{yc.diff}%</span>}
                      </td>
                      <td className="py-2.5 px-2 text-center text-[11px] font-bold text-gray-600">{oe.last_draw_date}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`font-mono text-xs font-bold ${oe.gap > oe.expected_gap ? "text-red-600" : "text-gray-500"}`}>{oe.gap}</span>
                        <span className="text-[9px] text-gray-400 ml-1">/~{oe.expected_gap}</span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        {isOverdue ? <span className="text-[9px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">OVERDUE +{oe.overdue_by}</span>
                          : oe.gap === 0 ? <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">RECENT</span>
                          : <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      </Block>

      {/* Block 3 — High/Low Split */}
      <Block n={3} title="High/Low Split — All 5 Balls" subtitle={`Low (<25) vs High (25+). Till 2025 (${data.rules.draws_till_2025}) vs 2026 (${data.rules.draws_2026}). Sorted by highest %.`}>
          <div className="overflow-x-auto" data-testid="high-low-rules">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-3">Pattern</th>
                  <th className="text-center py-2 px-2">Till 2025</th>
                  <th className="text-center py-2 px-2">2026</th>
                  <th className="text-center py-2 px-2">Diff</th>
                  <th className="text-center py-2 px-2">Last Seen</th>
                  <th className="text-center py-2 px-2">Gap</th>
                  <th className="text-center py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...(data.rules.hl_gap_analysis || [])].sort((a, b) => b.pct - a.pct).map(hl => {
                  const isOverdue = hl.overdue;
                  const maxCount = Math.max(...(data.rules.hl_gap_analysis || []).map(x => x.count));
                  return (
                    <tr key={hl.pattern} className={`border-b border-gray-100 ${isOverdue ? "bg-red-50" : ""}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold text-gray-800 w-14">{hl.pattern}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3 min-w-[40px]">
                            <div className={`h-3 rounded-full ${isOverdue ? "bg-red-500" : "bg-[#002FA7]"}`} style={{ width: `${(hl.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-2 text-center font-mono font-bold text-gray-600">{hl.till_2025_pct}%</td>
                      <td className="py-2.5 px-2 text-center font-mono font-bold text-violet-700">{hl.y2026_pct}%</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`font-mono font-bold text-xs px-2 py-0.5 rounded ${hl.diff > 0 ? "text-emerald-700 bg-emerald-50" : hl.diff < 0 ? "text-red-600 bg-red-50" : "text-gray-400"}`}>{hl.diff > 0 ? "+" : ""}{hl.diff}%</span>
                      </td>
                      <td className="py-2.5 px-2 text-center text-[11px] font-bold text-gray-600">{hl.last_draw_date}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`font-mono text-xs font-bold ${hl.gap > hl.expected_gap ? "text-red-600" : "text-gray-500"}`}>{hl.gap}</span>
                        <span className="text-[9px] text-gray-400 ml-1">/~{hl.expected_gap}</span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        {isOverdue ? <span className="text-[9px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">OVERDUE +{hl.overdue_by}</span>
                          : hl.gap === 0 ? <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">RECENT</span>
                          : <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      </Block>

      {/* Block 4 — Top Range Patterns */}
      <Block n={4} title="Top Range Patterns" subtitle="[1-10]-[11-20]-[21-30]-[31-40]-[41-50]. Gap = draws since last seen.">
        <div className="space-y-2" data-testid="range-patterns">
          {(data.rules.rp_gap_analysis || []).map(rp => {
            const maxCount = data.rules.range_patterns?.[0]?.count || 1;
            const isOverdue = rp.overdue;
            return (
              <div key={rp.pattern} className={`rounded-lg p-2.5 border ${isOverdue ? "border-red-300 bg-red-50" : "border-gray-100"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm w-24 text-right font-bold text-gray-800">{rp.pattern}</span>
                  <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                    <div className={`h-full rounded ${isOverdue ? "bg-red-500" : "bg-blue-400"}`} style={{ width: `${(rp.count / maxCount) * 100}%` }} />
                  </div>
                  <span className="font-mono text-xs w-12 text-right font-bold">{rp.pct}%</span>
                </div>
                <div className="flex items-center gap-3 ml-[104px] text-[10px]">
                  <span className="text-gray-500">Last: <strong className="text-gray-700">{rp.last_draw_date}</strong></span>
                  <span className={`font-bold ${rp.gap > rp.expected_gap ? "text-red-600" : "text-gray-500"}`}>Gap: {rp.gap}</span>
                  <span className="text-gray-400">Exp: ~{rp.expected_gap}</span>
                  {isOverdue && <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">OVERDUE +{rp.overdue_by}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </Block>

      {/* Block 5 — Validated Combinations (FINAL OUTPUT) */}
      <section className="rounded-lg overflow-hidden border border-indigo-300" data-testid="w28-block-5">
        <header className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm tracking-tight">
            <span className="inline-block px-2 py-0.5 bg-emerald-500 text-white rounded mr-2 text-[11px] font-black">Block 5 · Verdict</span>
            Validated Combinations — Never-drawn-before, filtered + prioritized
          </h3>
          <p className="text-[11px] text-white/70 mt-0.5">Filters: sum ({data.rules.sum_range.min}-{data.rules.sum_range.max}), spread, odd/even (1-4). Prioritized by overdue O/E + range patterns.</p>
        </header>
        <div className="bg-gradient-to-r from-indigo-700 to-blue-800 p-5 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Validated Combinations</h3></div>
            {data.uniqueness && (
              <div className="bg-white/10 backdrop-blur rounded-lg px-3 py-1.5 text-right" data-testid="uniqueness-badge">
                <div className="text-[10px] text-white/60 uppercase font-bold">Historical Uniqueness</div>
                <div className="text-sm font-black">{data.uniqueness.uniqueness_pct}% unique</div>
                <div className="text-[9px] text-white/50">{data.uniqueness.unique_combos}/{data.uniqueness.total_draws} draws — {data.uniqueness.repeated_combos} repeat(s)</div>
              </div>
            )}
          </div>
          <div className="overflow-x-auto" data-testid="validated-combos">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/70">
                  <th className="px-3 py-2 text-center">#</th>
                  <th className="px-3 py-2 text-center">Numbers</th>
                  <th className="px-3 py-2 text-center">Sum</th>
                  <th className="px-3 py-2 text-center">Spread</th>
                  <th className="px-3 py-2 text-center">Odds</th>
                  <th className="px-3 py-2 text-center">Range</th>
                  <th className="px-3 py-2 text-center">Due</th>
                </tr>
              </thead>
              <tbody>
                {(data.validated_combinations || []).map((c, idx) => (
                  <tr key={idx} className={idx === 0 ? 'bg-yellow-400/20' : idx % 2 === 0 ? 'bg-white/5' : ''} data-testid={`combo-${idx}`}>
                    <td className="px-3 py-2 border-b border-white/10 text-center font-bold">{idx + 1}</td>
                    <td className="px-3 py-2 border-b border-white/10 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {c.balls.map((b, i) => (
                          <span key={i} className="inline-block w-8 h-8 rounded-full bg-white text-[#002FA7] text-center leading-8 font-mono font-bold">{b}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 border-b border-white/10 font-mono text-center">{c.sum}</td>
                    <td className="px-3 py-2 border-b border-white/10 font-mono text-center">{c.spread}</td>
                    <td className="px-3 py-2 border-b border-white/10 font-mono text-center">{c.odds}O/{5 - c.odds}E</td>
                    <td className="px-3 py-2 border-b border-white/10 font-mono text-center">{c.range}</td>
                    <td className="px-3 py-2 border-b border-white/10 text-center">
                      <div className="flex gap-1 justify-center">
                        {c.oe_due && <span className="text-[9px] font-bold bg-amber-400/30 text-amber-200 px-1.5 py-0.5 rounded">OE</span>}
                        {c.rp_due && <span className="text-[9px] font-bold bg-red-400/30 text-red-200 px-1.5 py-0.5 rounded">RP</span>}
                        {!c.oe_due && !c.rp_due && <span className="text-white/30">-</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ========== Tab 2: Top Combos + Prediction (from W15) ========== */
function TopCombosTab({ w15, data }) {
  if (!w15) return null;

  const oeSorted = [...(w15.odd_even || [])].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0));
  const hlSorted = [...(w15.high_low || [])].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0));

  // Build 2025 vs 2026 data for combined combos
  const combos2025 = {};
  const combos2026 = {};
  const ball_keys = ["ball_1", "ball_2", "ball_3", "ball_4", "ball_5"];
  // We can derive this from the w15.combos and total info
  const totalDraws = w15.total_draws || 1;
  const draws2025 = data?.rules?.draws_till_2025 || 0;
  const draws2026 = data?.rules?.draws_2026 || 0;

  return (
    <div className="space-y-4">
      {/* Block A — Top Combined Patterns */}
      <Block n="A" title="Top Combined Patterns (Odd/Even + High/Low)" subtitle={`Till 2025 (${w15.draws_till_2025 || '-'}) vs 2026 (${w15.draws_2026 || '-'}). Sorted by total frequency.`}>
        <div className="overflow-x-auto" data-testid="top-combos-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left text-[10px]">#</th>
                <th className="px-2 py-2 text-left text-[10px]">Pattern</th>
                <th className="px-2 py-2 text-center text-[10px]">Till 2025</th>
                <th className="px-2 py-2 text-center text-[10px]">2026</th>
                <th className="px-2 py-2 text-center text-[10px]">Diff</th>
                <th className="px-2 py-2 text-center text-[10px]">Total</th>
                <th className="px-2 py-2 text-center text-[10px]">%</th>
                <th className="px-2 py-2 text-[10px]">Distribution</th>
              </tr>
            </thead>
            <tbody>
              {(w15.combos || []).map((c, idx) => (
                <tr key={c.pattern} className={idx === 0 ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx === 0 ? { borderLeft: '4px solid #002FA7' } : {}}>
                  <td className="px-2 py-1.5 border-b font-bold">{idx + 1}</td>
                  <td className="px-2 py-1.5 border-b font-mono font-bold">{c.pattern}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center">
                    <span className="font-bold text-gray-600">{c.till_2025_pct ?? '-'}%</span>
                    <span className="text-[9px] text-gray-400 ml-1">({c.till_2025_count ?? '-'})</span>
                  </td>
                  <td className="px-2 py-1.5 border-b font-mono text-center">
                    <span className="font-bold text-violet-700">{c.y2026_pct ?? '-'}%</span>
                    <span className="text-[9px] text-gray-400 ml-1">({c.y2026_count ?? '-'})</span>
                  </td>
                  <td className="px-2 py-1.5 border-b text-center">
                    {c.diff != null && (
                      <span className={`font-mono font-bold text-xs px-2 py-0.5 rounded ${c.diff > 0 ? "text-emerald-700 bg-emerald-50" : c.diff < 0 ? "text-red-600 bg-red-50" : "text-gray-400"}`}>
                        {c.diff > 0 ? "+" : ""}{c.diff}%
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 border-b font-mono text-center">{c.count}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{c.pct}%</td>
                  <td className="px-2 py-1.5 border-b">
                    <div className="bg-gray-100 rounded h-4 overflow-hidden">
                      <div className="h-full bg-[#002FA7] rounded" style={{ width: `${(c.count / (w15.combos[0]?.count || 1)) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>

      {/* Block B — Prediction Scoring Tables */}
      <Block n="B" title="Prediction Scoring — Historical % + Mean-Reversion + Gap Due" subtitle="Per-pattern score combining multiple signal windows">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PredictionTable items={oeSorted} title="Odd/Even — Prediction Scores" colorFn={(i) => i === 0 ? 'bg-[#002FA7]' : 'bg-blue-400'} draws2025={draws2025} draws2026={draws2026} />
          <PredictionTable items={hlSorted} title="High/Low — Prediction Scores" colorFn={(i) => i === 0 ? 'bg-emerald-600' : 'bg-emerald-400'} draws2025={draws2025} draws2026={draws2026} />
        </div>
      </Block>

      {/* Block C — Predicted Next-Draw Balance (FINAL OUTPUT) */}
      <section className="rounded-lg overflow-hidden border border-indigo-300" data-testid="w28-block-C" >
        <header className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm tracking-tight">
            <span className="inline-block px-2 py-0.5 bg-emerald-500 text-white rounded mr-2 text-[11px] font-black">Block C · Verdict</span>
            Predicted Next-Draw Balance
          </h3>
          <p className="text-[11px] text-white/70 mt-0.5">Final O/E + H/L predictions (top-ranked highlighted)</p>
        </header>
        <div className="bg-gradient-to-r from-indigo-700 to-blue-800 p-5 text-white" data-testid="pred-balance">
          <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Next Draw Balance</h3></div>
          <p className="text-xs text-white/70 mb-4">Score = Historical % + Multi-Window Mean Reversion (R10/R20/R50) + Gap-Based Due Appearance. Overdue patterns (gap ratio &gt; 1.0) get strong boost.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold mb-2">Odd/Even Prediction</h4>
              <div className="space-y-2">
                {oeSorted.map((item, idx) => (
                  <div key={item.pattern} className={`p-3 rounded ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-lg">{item.pattern}</span>
                      <span className={`font-mono font-bold text-xl ${idx === 0 ? 'text-sky-800' : ''}`}>{item.pred_score}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px]">
                      <span>{item.pct}% hist</span>
                      <span className={item.gap_ratio >= 1 ? 'text-red-300 font-bold' : ''}>Gap: {item.current_gap}/{item.expected_gap} ({item.gap_ratio}x)</span>
                      <span className={item.reversion_bonus > 0 ? 'text-green-300' : item.reversion_bonus < 0 ? 'text-red-300' : ''}>Rev: {item.reversion_bonus > 0 ? '+' : ''}{item.reversion_bonus}</span>
                      <span className={item.gap_bonus > 0 ? 'text-green-300' : item.gap_bonus < 0 ? 'text-red-300' : ''}>Gap: {item.gap_bonus > 0 ? '+' : ''}{item.gap_bonus}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-2">High/Low Prediction</h4>
              <div className="space-y-2">
                {hlSorted.map((item, idx) => (
                  <div key={item.pattern} className={`p-3 rounded ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-lg">{item.pattern}</span>
                      <span className={`font-mono font-bold text-xl ${idx === 0 ? 'text-sky-800' : ''}`}>{item.pred_score}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px]">
                      <span>{item.pct}% hist</span>
                      <span className={item.gap_ratio >= 1 ? 'text-red-300 font-bold' : ''}>Gap: {item.current_gap}/{item.expected_gap} ({item.gap_ratio}x)</span>
                      <span className={item.reversion_bonus > 0 ? 'text-green-300' : item.reversion_bonus < 0 ? 'text-red-300' : ''}>Rev: {item.reversion_bonus > 0 ? '+' : ''}{item.reversion_bonus}</span>
                      <span className={item.gap_bonus > 0 ? 'text-green-300' : item.gap_bonus < 0 ? 'text-red-300' : ''}>Gap: {item.gap_bonus > 0 ? '+' : ''}{item.gap_bonus}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PredictionTable({ items, title, colorFn }) {
  const maxCount = Math.max(...items.map(i => i.count));
  return (
    <div className="bg-white border rounded p-4">
      <h3 className="font-bold text-gray-900 mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left font-bold uppercase text-[10px]">Pattern</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]">Count</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]">Hist %</th>
              <th className="px-2 py-2 font-bold uppercase text-[10px]">Bar</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" title="Draws since last">Gap</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" title="Expected gap">Exp</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" title="Due ratio">Due</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" title="R10/R20/R50">Recent</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]">Rev</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]">Gap+</th>
              <th className="px-2 py-2 text-center font-bold uppercase text-[10px]">Score</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const isTop = idx === 0;
              const isOverdue = item.gap_ratio > 1.0;
              return (
                <tr key={item.pattern} className={isTop ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={isTop ? { borderLeft: '4px solid #002FA7', backgroundColor: 'rgba(0, 47, 167, 0.08)' } : {}}>
                  <td className={`px-2 py-1.5 border-b font-mono font-bold ${isTop ? 'text-[#002FA7]' : ''}`}>{item.pattern}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center">{item.count}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{item.pct}%</td>
                  <td className="px-2 py-1.5 border-b">
                    <div className="bg-gray-100 rounded h-4 overflow-hidden">
                      <div className={`h-full rounded ${colorFn(idx)}`} style={{ width: `${(item.count / maxCount) * 100}%` }} />
                    </div>
                  </td>
                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>{item.current_gap}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center text-gray-500">{item.expected_gap}</td>
                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${item.gap_ratio >= 2 ? 'text-red-700 bg-red-50' : item.gap_ratio >= 1.2 ? 'text-orange-600' : isOverdue ? 'text-yellow-600' : 'text-gray-500'}`}>{item.gap_ratio}x</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center text-gray-600">{item.recent_10}/{item.recent_20}/{item.recent_50}</td>
                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${item.reversion_bonus > 0 ? 'text-green-600' : item.reversion_bonus < 0 ? 'text-red-500' : 'text-gray-400'}`}>{item.reversion_bonus > 0 ? '+' : ''}{item.reversion_bonus}</td>
                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold ${item.gap_bonus > 0 ? 'text-green-600' : item.gap_bonus < 0 ? 'text-red-500' : 'text-gray-400'}`}>{item.gap_bonus > 0 ? '+' : ''}{item.gap_bonus}</td>
                  <td className={`px-2 py-1.5 border-b font-mono text-center font-bold text-lg ${isTop ? 'text-[#002FA7]' : ''}`}>{item.pred_score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ========== Tab 3: Last 20 Draws ========== */
function RecentDrawsTab({ w15 }) {
  if (!w15) return null;
  return (
    <div className="space-y-4">
      <Block n="R" title="Last 20 Draws — Pattern History" subtitle="Each draw's structural pattern — Odd/Even split and High/Low split.">
        <div className="overflow-x-auto" data-testid="recent-draws-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-[10px]">Draw</th>
                <th className="px-2 py-2 text-[10px]">Date</th>
                <th className="px-2 py-2 text-[10px]">Balls</th>
                <th className="px-2 py-2 text-center text-[10px]">Odd/Even</th>
                <th className="px-2 py-2 text-center text-[10px]">High/Low</th>
              </tr>
            </thead>
            <tbody>
              {(w15.last_20 || []).map((d, idx) => (
                <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-2 py-1.5 border-b font-mono font-bold">#{d.draw}</td>
                  <td className="px-2 py-1.5 border-b text-gray-700">{d.date}</td>
                  <td className="px-2 py-1.5 border-b">
                    <div className="flex gap-1">
                      {d.balls.map((b, i) => (
                        <span key={i} className="inline-flex w-7 h-7 rounded-full bg-blue-600 text-white items-center justify-center font-mono font-bold text-[10px]">{b}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{d.oe}</td>
                  <td className="px-2 py-1.5 border-b font-mono text-center font-bold">{d.hl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>
    </div>
  );
}
